import { component } from "picoapp";
import {
  PAGE_CAROUSEL_OPTIONS,
  PAGINATION_ACTIVE_CLASS,
  PAGINATION_INACTIVE_CLASS,
  SLIDE_FADE_CLASS,
} from "../tbsp-carousel--constants";
import { type Context } from "@tablescape/state";
import {
  EventInterface,
  type Components,
  type Splide,
  type Options,
} from "@splidejs/splide";

/**
 * Generates a unique carousel ID
 */
function generateCarouselId(): string {
  return `carousel-${Math.random().toString(36).substring(2, 11)}`;
}

/**
 * Announces a message to screen readers via aria-live region
 */
function announceToScreenReader(message: string): void {
  let liveRegion = document.getElementById("aria-live-polite");
  if (!liveRegion) {
    liveRegion = document.createElement("div");
    liveRegion.id = "aria-live-polite";
    liveRegion.setAttribute("aria-live", "polite");
    liveRegion.setAttribute("aria-atomic", "true");
    liveRegion.className =
      "absolute w-px h-px overflow-hidden whitespace-nowrap border-0 m-0 p-0";
    liveRegion.style.left = "-10000px";
    liveRegion.style.clip = "rect(0, 0, 0, 0)";
    document.body.appendChild(liveRegion);
  }
  liveRegion.textContent = message;
}

/**
 * Sets up ARIA attributes on the carousel container
 */
function setupCarouselAria(
  node: HTMLElement,
  carouselId: string,
  totalSlides: number
): void {
  node.setAttribute("role", "region");
  node.setAttribute("aria-roledescription", "carousel");
  node.setAttribute(
    "aria-label",
    node.getAttribute("aria-label") || `Carousel with ${totalSlides} slides`
  );
  node.id = node.id || carouselId;

  // Make carousel focusable for keyboard navigation
  if (!node.hasAttribute("tabindex")) {
    node.setAttribute("tabindex", "0");
  }
}

/**
 * Sets up keyboard event handlers for carousel navigation
 */
function setupKeyboardNavigation(
  node: HTMLElement,
  splide: Splide,
  hasAutoplay: boolean
): void {
  node.addEventListener("keydown", (event: KeyboardEvent) => {
    switch (event.key) {
      case "ArrowLeft":
        event.preventDefault();
        splide.go("-1");
        break;
      case "ArrowRight":
        event.preventDefault();
        splide.go("+1");
        break;
      case " ":
        // Space key toggles autoplay if enabled
        if (hasAutoplay) {
          event.preventDefault();
          const autoplayComponent = splide.Components.Autoplay;
          if (autoplayComponent) {
            if (autoplayComponent.isPaused()) {
              autoplayComponent.play();
              announceToScreenReader("Carousel playing");
            } else {
              autoplayComponent.pause();
              announceToScreenReader("Carousel paused");
            }
          }
        }
        break;
    }
  });
}

export function VideoAutoPlay(
  Splide: Splide,
  Components: Components,
  options: Options
) {
  const { on } = EventInterface(Splide);
  const { Controller, Elements, Slides } = Components;
  const autoPlayDuration = options.interval || 5;

  function autoPlayVideo() {
    const currentIndex = Controller.getIndex();
    const currentSlide = Slides.getAt(currentIndex);
    const currentVideo =
      currentSlide?.slide.querySelector<HTMLVideoElement>("video");
    if (!currentVideo) return;

    currentVideo.play();
  }

  function setVideoAttributes() {
    const { root } = Elements;
    const videos = root.querySelectorAll<HTMLVideoElement>("video");

    videos.forEach((video) => {
      video.preload = "none";
      video.muted = true;
      video.autoplay = false;
      video.playsInline = true;

      const goToNextVideo = () => {
        video.pause();
        Controller.go("+1");
      };

      const setVideoIntervals = () => {
        if (video.currentTime * 1000 >= autoPlayDuration) {
          if (!video.paused) {
            goToNextVideo();
          }
        }
      };

      video.addEventListener("ended", goToNextVideo);
      video.addEventListener("timeupdate", setVideoIntervals);
    });
  }

  function mount() {
    let activeCount = 0;
    setVideoAttributes();
    on("intersection:in", () => {
      autoPlayVideo();
    });

    on("intersection:out", () => {
      const currentIndex = Controller.getIndex();
      const currentSlide = Slides.getAt(currentIndex);
      const video =
        currentSlide?.slide.querySelector<HTMLVideoElement>("video");
      video?.pause();
    });

    on("active", () => {
      if (activeCount > 0) {
        autoPlayVideo();
      } else {
        activeCount++;
      }
    });

    on("inactive", ({ slide }) => {
      const video = slide.querySelector<HTMLVideoElement>("video");
      if (video) {
        video.pause();
        video.currentTime = 0;
      }
    });
  }

  return {
    mount,
  };
}

export const paginatedCarousel = component(
  async (node: HTMLElement, context: Context) => {
    const isVideoEnabled = node.dataset.videoEnabled === "true";
    const isPausingEnabled = node.dataset.pausingEnabled === "true";
    const isFadeEnabled = node.dataset.fadeInactive === "true";
    const isLoopingEnabled = node.dataset.loopingEnabled === "true";
    const isPaginationEnabled = node.dataset.paginationEnabled === "true";
    const isVideoAutoPlayEnabled = node.dataset.videoAutoplayEnabled === "true";
    const rootMargin = node.dataset.rootMargin ?? "200px";
    const threshold = node.dataset.threshold
      ? parseInt(node.dataset.threshold)
      : 0;
    const persistIntersection = node.dataset.persistIntersection === "true";
    const slideGap = node.dataset.slideGap;
    const mdSlideGap = node.dataset.mdSlideGap;
    const paddingLeft = node.dataset.paddingLeft;
    const paddingRight = node.dataset.paddingRight;
    const mdPaddingLeft = node.dataset.mdPaddingLeft;
    const mdPaddingRight = node.dataset.mdPaddingRight;

    const [{ default: Splide }, { Intersection }] = await Promise.all([
      import("@splidejs/splide"),
      import("@splidejs/splide-extension-intersection"),
      import("@splidejs/splide/css/core"),
    ]);

    const derivedGap = slideGap
      ? parseInt(slideGap)
      : PAGE_CAROUSEL_OPTIONS.gap;

    // Create padding object from data attributes
    const derivedPadding = {
      left: paddingLeft ? parseInt(paddingLeft) : 0,
      right: paddingRight ? parseInt(paddingRight) : 0,
    };

    const splide = new Splide(node, {
      ...PAGE_CAROUSEL_OPTIONS,
      type: isLoopingEnabled ? "loop" : PAGE_CAROUSEL_OPTIONS.type,
      pagination: isPaginationEnabled,
      gap: derivedGap,
      padding: derivedPadding,

      breakpoints: {
        ...PAGE_CAROUSEL_OPTIONS.breakpoints,
        768: {
          ...PAGE_CAROUSEL_OPTIONS.breakpoints[768],
          gap: mdSlideGap ? parseInt(mdSlideGap) : derivedGap,
          padding: {
            left: mdPaddingLeft ? parseInt(mdPaddingLeft) : derivedPadding.left,
            right: mdPaddingRight
              ? parseInt(mdPaddingRight)
              : derivedPadding.right,
          },
        },
      },

      intersection: {
        rootMargin,
        threshold,
        once: !persistIntersection,
      },
    });

    if (isVideoEnabled) {
      splide.on("intersection:in", function () {
        splide.on("active", ({ slide }) => {
          const video = slide.querySelector("video");

          if (video && isPausingEnabled) {
            video.play().catch();
          }
        });

        splide.on("inactive", ({ slide }) => {
          const video = slide.querySelector("video");

          if (video && isPausingEnabled) {
            video.muted = true;
            video.pause();
          }
        });

        if (!isVideoAutoPlayEnabled) {
          node.querySelectorAll("video").forEach((video) => {
            video.preload = "auto";
          });
        }
      });

      context.on("tbsp:button-clicked", ({ action, target }) => {
        // Disable autoplay and video slides when modal is toggled
        if (action === "toggle-modal") {
          const { Controller, Slides, VideoAutoPlay } = splide.Components;
          if (VideoAutoPlay) {
            const currentIndex = Controller.getIndex();
            const activeSlide = Slides.getAt(currentIndex);
            const video =
              activeSlide?.slide.querySelector<HTMLVideoElement>("video");
            if (!video) return;
            video.pause();
            video.currentTime = 0;
          }

          const modal = document.querySelector(
            `[role='dialog'][data-tbsp-target='${target}']`
          );

          if (modal) {
            const modalVideo = modal.querySelector("video");
            if (modalVideo) {
              const resumeAutoPlayVideo = () => {
                const currentIndex = Controller.getIndex();
                const activeSlide = Slides.getAt(currentIndex);
                const video =
                  activeSlide?.slide.querySelector<HTMLVideoElement>("video");
                video?.play();
                modalVideo.removeEventListener("pause", resumeAutoPlayVideo);
              };
              modalVideo.addEventListener("pause", resumeAutoPlayVideo);
            }
          }
        }
      });
    }

    if (isFadeEnabled) {
      node
        .querySelectorAll(".splide__slide")
        .forEach((slide) => slide.classList.add(SLIDE_FADE_CLASS));

      splide.on("active", ({ slide }) => {
        slide.classList.remove(SLIDE_FADE_CLASS);
      });

      splide.on("inactive", ({ slide }) => {
        slide.classList.add(SLIDE_FADE_CLASS);
      });

      splide.on("click", ({ index }) => {
        splide.go(index);
      });
    }

    if (isPaginationEnabled) {
      splide.on("pagination:updated", (_, prev, curr) => {
        prev.button.classList.add(PAGINATION_INACTIVE_CLASS);
        prev.button.classList.remove(PAGINATION_ACTIVE_CLASS);

        curr.button.classList.add(PAGINATION_ACTIVE_CLASS);
        curr.button.classList.remove(PAGINATION_INACTIVE_CLASS);
      });
    }

    if (isVideoAutoPlayEnabled) {
      splide.mount({
        Intersection,
        VideoAutoPlay,
      });
    } else {
      splide.mount({
        Intersection,
      });
    }

    // Set up ARIA attributes for accessibility
    const carouselId = generateCarouselId();
    const totalSlides = splide.length;
    setupCarouselAria(node, carouselId, totalSlides);

    // Set up keyboard navigation
    const hasAutoplay = isVideoAutoPlayEnabled;
    setupKeyboardNavigation(node, splide, hasAutoplay);

    // Announce slide changes to screen readers
    splide.on("moved", (newIndex: number) => {
      announceToScreenReader(`Slide ${newIndex + 1} of ${totalSlides}`);
    });
  }
);
